import { Banner } from '@moniebook-web/components/molecules/banner';
import { AlertLine } from '@moniebook-web/icons-v2/system/alert-line';
import { RefreshLine } from '@moniebook-web/icons-v2/system/refresh-line';
import { SYSTEM_BANNER_ID } from '@moniebook-web/utils';
import { WifiLow, WifiOff } from '@register-ui/components';
import { useNetworkState, useSlowNetworkState } from '@register-ui/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { type PropsWithChildren, useMemo } from 'react';
import {
    BackgroundItemsSyncIndicator,
    useBackgroundItemsSyncIndicator,
} from './background-items-sync-indicator';
import {
    StockSyncIndicator,
    useStockSyncIndicator,
} from './stock-sync-indicator';
import {
    UnsyncedSalesSyncIndicator,
    useUnsyncedSalesSyncIndicator,
} from './unsynced-sales-sync-indicator';
import { useServiceWorkerUpdate } from './use-auto-update';

export function SystemBanner() {
    const { isOnline } = useNetworkState();
    const { isSlowNetwork } = useSlowNetworkState();
    const { needRefresh: hostUpdateAvailable, updateServiceWorker } =
        useServiceWorkerUpdate();

    const { stockStatus, stockVariant, showStockBanner } =
        useStockSyncIndicator();

    const {
        backgroundItemsStatus,
        backgroundItemsVariant,
        showBackgroundItemsBanner,
        retry: retryBackgroundItemsSync,
    } = useBackgroundItemsSyncIndicator();

    const {
        unsyncedSalesCount,
        unsyncedSalesStatus,
        unsyncedSalesVariant,
        unsyncedSalesFill,
        showUnsyncedSalesBanner,
        syncUnsyncedSales,
    } = useUnsyncedSalesSyncIndicator();

    const { bannerContent, bannerVariant, bannerFill } = useMemo(() => {
        if (showUnsyncedSalesBanner) {
            return {
                bannerVariant: unsyncedSalesVariant,
                bannerContent: (
                    <UnsyncedSalesSyncIndicator
                        status={unsyncedSalesStatus}
                        count={unsyncedSalesCount}
                        onSync={syncUnsyncedSales}
                    />
                ),
                bannerFill: unsyncedSalesFill ?? ('solid' as const),
            };
        }

        if (!isOnline) {
            return {
                bannerVariant: 'error' as const,
                bannerContent: <NoInternetBannerContent />,
            };
        }

        if (hostUpdateAvailable) {
            return {
                bannerContent: (
                    <NewUpdateBannerContent onUpdate={updateServiceWorker} />
                ),
            };
        }

        if (showStockBanner) {
            return {
                bannerVariant: stockVariant,
                bannerContent: <StockSyncIndicator status={stockStatus} />,
                bannerFill: 'ghost' as const,
            };
        }

        if (showBackgroundItemsBanner) {
            return {
                bannerVariant: backgroundItemsVariant,
                bannerContent: (
                    <BackgroundItemsSyncIndicator
                        status={backgroundItemsStatus}
                        onRetry={retryBackgroundItemsSync}
                    />
                ),
                bannerFill: 'ghost' as const,
            };
        }

        if (isSlowNetwork) {
            return { bannerContent: <SlowNetworkBannerContent /> };
        }

        return {};
    }, [
        isOnline,
        isSlowNetwork,
        hostUpdateAvailable,
        updateServiceWorker,
        stockStatus,
        showStockBanner,
        stockVariant,
        showBackgroundItemsBanner,
        backgroundItemsVariant,
        backgroundItemsStatus,
        retryBackgroundItemsSync,
        showUnsyncedSalesBanner,
        unsyncedSalesVariant,
        unsyncedSalesStatus,
        unsyncedSalesCount,
        unsyncedSalesFill,
        syncUnsyncedSales,
    ]);

    return (
        <AnimatePresence>
            {bannerContent ? (
                <Wrapper>
                    <Banner
                        content={bannerContent}
                        variant={bannerVariant ?? 'warning'}
                        isDismissible={false}
                        fill={bannerFill ?? 'solid'}
                        className='h-40 min-h-0 px-24'
                    />
                </Wrapper>
            ) : null}
        </AnimatePresence>
    );
}

function Wrapper({ children }: Readonly<PropsWithChildren>) {
    return (
        <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{
                opacity: 1,
                height: 'auto',
                transition: {
                    height: { duration: 0.2 },
                    opacity: { duration: 0.2, delay: 0.1 },
                },
            }}
            exit={{
                opacity: 0,
                height: 0,
                transition: {
                    height: { duration: 0.2 },
                    opacity: { duration: 0 },
                },
            }}
            id={SYSTEM_BANNER_ID}>
            {children}
        </motion.div>
    );
}

function NoInternetBannerContent() {
    return (
        <div className='flex items-center gap-16 px-8'>
            <WifiOff />
            <span>No internet connection.</span>
        </div>
    );
}

function SlowNetworkBannerContent() {
    return (
        <div className='flex items-center gap-16 px-8'>
            <WifiLow />
            <span>Slow network detected. Some actions may take longer.</span>
        </div>
    );
}

interface NewUpdateBannerContentProps {
    onUpdate?: VoidFunction;
}

function NewUpdateBannerContent(props: Readonly<NewUpdateBannerContentProps>) {
    const { onUpdate } = props;

    return (
        <div className='flex items-center justify-between gap-10'>
            <div className='flex items-center gap-16 px-8'>
                <AlertLine />

                <span>A new version of Moniebook is available.</span>
            </div>

            <button
                className='flex items-center gap-8 font-medium'
                onClick={onUpdate}>
                <RefreshLine />
                <span>Reload page</span>
            </button>
        </div>
    );
}
